import React from "react"

import { PrettyProviderName } from "@logic/lib/loans"
import type { QueryClient } from "@tanstack/react-query"
import type { TFunction } from "i18next"

import { queryKeys } from "~/api/cache"
import type { LoanProductPartnerOptionDto } from "~/api/dto/loan-product-partner.dto"
import type { UpdateProposalFinanceDto } from "~/api/dto/proposal.dto"
import { checkIfLoanProductIsTpo } from "~/containers/Proposal/state/loan-products/tpo-loan-product"
import type { UseProposal } from "~/containers/Proposal/state/proposal/proposal-context"
import type {
  CreateLoanProductInput,
  LoanProduct,
  LoanProvider,
} from "~/graphql/client"
import { LoanProvider as LoanProviderEnum } from "~/graphql/client"
import { FinanceType } from "~/utils/finance/constants"
import { isDefined } from "~/utils/predicates"

import {
  getGoodleapLoanTypeFromGroup,
  getSungageLoanTypeFromGroup,
  loanProductsToOptions,
  makeOptionName,
} from "./utils"

type ProposalContextValue = ReturnType<UseProposal>

type FinancingSelectionStateInput = {
  loanProducts: LoanProduct[]
  partnerOptions: LoanProductPartnerOptionDto[]
  proposal: ProposalContextValue
  selectedFinanceType: string
  setSelectedFinanceType: React.Dispatch<React.SetStateAction<string>>
  t: TFunction
}

type FinancingSelectionSaveInput = {
  createLoanProduct: {
    mutateAsync: (variables: {
      input: CreateLoanProductInput
    }) => Promise<unknown>
  }
  onError: (error: unknown) => void
  proposal: ProposalContextValue
  queryClient: QueryClient
  selectedDownPayment: number | null
  selectedFinanceProductId: string | null
  selectedProduct: LoanProduct | null
  selectedUpdatedPricePerWatt: number | null
  setHasUserModifiedFunding: React.Dispatch<React.SetStateAction<boolean>>
  setSaveError: React.Dispatch<React.SetStateAction<string | null>>
  updateProposalFinanceProduct: {
    mutateAsync: (variables: {
      id: string
      data: UpdateProposalFinanceDto
    }) => Promise<unknown>
  }
}

type FinancingSelectionState = {
  disableDecimal: boolean
  formatLoanTypeLabel: (value: string) => string
  fundingGroupOptions: string[]
  handleChangeLightReachProduct: (
    productId: string | null,
    pricePerWatt?: number,
  ) => void
  handleDownPaymentChange: (value: number | null) => void
  handleFinancingTypeChange: (financeType: string) => void
  handleFundingGroupChange: (fundingGroup: string) => void
  isTpo: boolean
  loanProductOptions: string[]
  loanTypes: string[]
  saveError: string | null
  selectedDownPayment: number | null
  selectedFinanceProductId: string | null
  selectedFinanceType: string
  selectedFinanceTypeLabel: string
  selectedProduct: LoanProduct | null
  selectedProductGroup: string | null
  selectedUpdatedPricePerWatt: number | null
  setHasUserModifiedFunding: React.Dispatch<React.SetStateAction<boolean>>
  setSaveError: React.Dispatch<React.SetStateAction<string | null>>
  updateSelected: (options: string[]) => void
}

type FinancingSelectionSave = {
  handleSaveLoanProduct: () => Promise<void>
}

type FinanceUpdatePayloadInput = {
  selectedFinanceType: string
  selectedFundingGroup: string | null
  selectedDownPayment: number | null
  selectedFinanceProductId: string | null
  selectedProduct: LoanProduct | null
  selectedUpdatedPricePerWatt: number | null
}

export const buildFinanceUpdatePayload = (
  input: FinanceUpdatePayloadInput,
): UpdateProposalFinanceDto => {
  const {
    selectedFinanceType,
    selectedFundingGroup,
    selectedDownPayment,
    selectedFinanceProductId,
    selectedProduct,
    selectedUpdatedPricePerWatt,
  } = input

  const shouldClearDownPayment = [
    FinanceType.CASH,
    FinanceType.CONCERT,
    FinanceType.CREDIT_HUMAN,
    FinanceType.LIGHT_REACH,
  ].includes(selectedFinanceType as FinanceType)

  return {
    financeType: selectedFinanceType,
    fundingGroup: selectedFundingGroup,
    selectedLoanProductId: selectedProduct?.id ?? null,
    financeProductId: selectedFinanceProductId,
    pricePerWatt: selectedUpdatedPricePerWatt,
    downPayment: shouldClearDownPayment ? null : (selectedDownPayment ?? null),
  }
}

const buildCreateLoanProductInput = (
  product: LoanProduct,
): CreateLoanProductInput => {
  return {
    activatedAt: product.activatedAt,
    createdAt: product.createdAt,
    id: product.id,
    introPaymentFactor: product.introPaymentFactor,
    maximumLoanAmount: product.maximumLoanAmount,
    minimumCreditScore: product.minimumCreditScore,
    minimumLoanAmount: product.minimumLoanAmount,
    powurOsCurrentDealerFee: product.powurOsCurrentDealerFee,
    powurOsCurrentPowurMargin: product.powurOsCurrentPowurMargin,
    powurOsDealerFeePlusMargin: product.powurOsDealerFeePlusMargin,
    productName: product.productName,
    provider: product.provider,
    providerFee: product.providerFee,
    providerId: product.providerId,
    providerMeta: product.providerMeta,
    providerName: product.providerName,
    rate: product.rate,
    statesActive: product.statesActive,
    statesPossible: product.statesPossible,
    syncHistory: product.syncHistory,
    term: product.term,
    updatedAt: product.updatedAt,
    loanType: product.loanType,
  }
}

export const useFinancingSelectionState = ({
  loanProducts,
  partnerOptions,
  proposal,
  selectedFinanceType,
  setSelectedFinanceType,
  t,
}: FinancingSelectionStateInput): FinancingSelectionState => {
  const [selectedProduct, setSelectedProduct] =
    React.useState<LoanProduct | null>(
      proposal.local.selectedLoanProduct ?? null,
    )

  const [selectedFinanceProductId, setSelectedFinanceProductId] = React.useState<
    string | null
  >(proposal.local.financeProductId)

  const [selectedUpdatedPricePerWatt, setSelectedUpdatedPricePerWatt] =
    React.useState<number | null>(null)

  const [selectedDownPayment, setSelectedDownPayment] = React.useState<
    number | null
  >(proposal.local.downPayment ?? null)

  const [hasUserModifiedFunding, setHasUserModifiedFunding] =
    React.useState(false)

  const [saveError, setSaveError] = React.useState<string | null>(null)

  const [selectedProductGroup, setSelectedProductGroup] = React.useState<
    string | null
  >(() => proposal.local.fundingGroup ?? null)

  const partnerOptionByFinanceType = React.useMemo(() => {
    return new Map(partnerOptions.map((option) => [option.financeType, option]))
  }, [partnerOptions])

  const fundingGroupOptions = React.useMemo(() => {
    return (
      partnerOptionByFinanceType
        .get(selectedFinanceType)
        ?.fundingGroups?.map((group) => group.value) ?? []
    )
  }, [partnerOptionByFinanceType, selectedFinanceType])

  const isTpo = checkIfLoanProductIsTpo(selectedProduct)

  const disableDecimal =
    selectedFinanceType === FinanceType.CASH ||
    selectedFinanceType === FinanceType.CONCERT

  const loanTypes = React.useMemo(
    () => partnerOptions.map((option) => option.financeType),
    [partnerOptions],
  )

  React.useEffect(() => {
    const localSelected = proposal.local.selectedLoanProduct
    if (!localSelected) return
    if (selectedProduct?.id === localSelected.id) return
    if (hasUserModifiedFunding) return

    const fromQuery = loanProducts.find((lp) => lp.id === localSelected.id)
    setSelectedProduct(fromQuery ?? localSelected)
  }, [
    hasUserModifiedFunding,
    loanProducts,
    proposal.local.selectedLoanProduct,
    selectedProduct?.id,
  ])

  React.useEffect(() => {
    if (hasUserModifiedFunding) return

    const nextFinanceType = proposal.local.financeType ?? FinanceType.CASH
    if (selectedFinanceType !== nextFinanceType) {
      setSelectedFinanceType(nextFinanceType)
    }

    const nextFundingGroup = proposal.local.fundingGroup ?? null
    if (selectedProductGroup !== nextFundingGroup) {
      setSelectedProductGroup(nextFundingGroup)
    }
  }, [
    hasUserModifiedFunding,
    proposal.local.financeType,
    proposal.local.fundingGroup,
    selectedFinanceType,
    selectedProductGroup,
    setSelectedFinanceType,
  ])

  const formatLoanTypeLabel = React.useCallback(
    (value: string): string => {
      const labelKey = partnerOptionByFinanceType.get(value)?.labelKey ?? null

      if (labelKey) {
        const translated = t(labelKey as never) as string
        if (translated !== labelKey) {
          return translated
        }
      }

      if (value === FinanceType.CASH) {
        return t(`cash`)
      }

      return PrettyProviderName[value as LoanProvider] ?? value
    },
    [partnerOptionByFinanceType, t],
  )

  const selectedFinanceTypeLabel = formatLoanTypeLabel(selectedFinanceType)

  const providerFilter = React.useMemo(
    () =>
      Object.values(LoanProviderEnum).includes(
        selectedFinanceType as LoanProviderEnum,
      )
        ? selectedFinanceType
        : FinanceType.CASH,
    [selectedFinanceType],
  )

  const filteredLoanProducts = React.useMemo(() => {
    if (selectedFinanceType === FinanceType.GOODLEAP) {
      const targetLoanType = getGoodleapLoanTypeFromGroup(selectedProductGroup)

      if (!targetLoanType) {
        return []
      }

      return loanProducts.filter(
        (product) => product.loanType === targetLoanType,
      )
    }

    if (selectedFinanceType === FinanceType.SUNGAGE) {
      const targetLoanType = getSungageLoanTypeFromGroup(selectedProductGroup)

      if (!targetLoanType) {
        return []
      }

      return loanProducts.filter(
        (product) => product.loanType === targetLoanType,
      )
    }

    return loanProducts
  }, [loanProducts, selectedFinanceType, selectedProductGroup])

  const loanProductOptions = React.useMemo(
    () => loanProductsToOptions(filteredLoanProducts, providerFilter),
    [filteredLoanProducts, providerFilter],
  )

  const updateSelected = React.useCallback(
    (options: string[]) => {
      const newlySelectedLoanProducts = options
        .map((name) =>
          loanProducts.find((item) => makeOptionName(item) === name),
        )
        .filter(isDefined)

      setSelectedProduct(newlySelectedLoanProducts[0])
      setHasUserModifiedFunding(true)
      setSaveError(null)

      const downPayment = !newlySelectedLoanProducts.length
        ? 0
        : Number(proposal.local.downPayment ?? 0)

      const nextDownPayment = downPayment === 0 ? null : downPayment
      setSelectedDownPayment(nextDownPayment)

      const isEnfin = newlySelectedLoanProducts.some(
        (loanProvider) => loanProvider.provider === LoanProviderEnum.enfin,
      )

      if (isEnfin) {
        const allIncentives = proposal.local.incentives
        if (allIncentives === null) return
        const enfinIncentives = allIncentives.map((i) => {
          const isFederal = i.incentive.jurisdiction === `federal`

          return {
            ...i,
            applyToLoan: isFederal,
          }
        })
        proposal.setLocal((curr) => ({
          ...curr,
          incentives: enfinIncentives,
        }))
      }
    },
    [loanProducts, proposal],
  )

  const handleChangeLightReachProduct = React.useCallback(
    (productId: string | null, pricePerWatt?: number) => {
      setSelectedFinanceProductId(productId)
      setSelectedUpdatedPricePerWatt(pricePerWatt ?? null)
      setHasUserModifiedFunding(true)
      setSaveError(null)
    },
    [],
  )

  const handleFinancingTypeChange = React.useCallback(
    (financeType: string) => {
      setSelectedProduct(null)
      setHasUserModifiedFunding(true)
      setSaveError(null)
      const nextFinanceType = (() => {
        if (
          financeType === FinanceType.LIGHT_REACH ||
          financeType === `LightReach`
        ) {
          return FinanceType.LIGHT_REACH
        }

        if (financeType === FinanceType.CASH) {
          return FinanceType.CASH
        }

        return financeType
      })()

      setSelectedFinanceType(nextFinanceType)

      const requiresFundingGroup =
        partnerOptionByFinanceType.get(nextFinanceType)?.requiresFundingGroup ??
        false

      if (!requiresFundingGroup) {
        setSelectedProductGroup(null)
      }

      if (
        nextFinanceType === FinanceType.CASH ||
        nextFinanceType === FinanceType.CONCERT ||
        nextFinanceType === FinanceType.CREDIT_HUMAN ||
        nextFinanceType === FinanceType.LIGHT_REACH
      ) {
        setSelectedDownPayment(null)
        setSelectedFinanceProductId(null)
        setSelectedUpdatedPricePerWatt(null)
      }
    },
    [partnerOptionByFinanceType, setSelectedFinanceType],
  )

  const handleFundingGroupChange = React.useCallback((fundingGroup: string) => {
    setSelectedProductGroup(fundingGroup)
    setSelectedProduct(null)
    setSelectedFinanceProductId(null)
    setHasUserModifiedFunding(true)
    setSaveError(null)
  }, [])

  const handleDownPaymentChange = React.useCallback((value: number | null) => {
    setSelectedDownPayment(value === 0 ? null : value)
    setSaveError(null)
  }, [])

  return {
    disableDecimal,
    formatLoanTypeLabel,
    fundingGroupOptions,
    handleChangeLightReachProduct,
    handleDownPaymentChange,
    handleFinancingTypeChange,
    handleFundingGroupChange,
    isTpo,
    loanProductOptions,
    loanTypes,
    saveError,
    selectedDownPayment,
    selectedFinanceProductId,
    selectedFinanceType,
    selectedFinanceTypeLabel,
    selectedProduct,
    selectedProductGroup,
    selectedUpdatedPricePerWatt,
    setHasUserModifiedFunding,
    setSaveError,
    updateSelected,
  }
}

export const useFinancingSelectionSave = ({
  createLoanProduct,
  onError,
  proposal,
  queryClient,
  selectedDownPayment,
  selectedFinanceProductId,
  selectedProduct,
  selectedUpdatedPricePerWatt,
  setHasUserModifiedFunding,
  setSaveError,
  updateProposalFinanceProduct,
}: FinancingSelectionSaveInput): FinancingSelectionSave => {
  const handleSaveLoanProduct = React.useCallback(async () => {
    const payload = buildFinanceUpdatePayload({
      selectedFinanceType: proposal.local.financeType ?? FinanceType.CASH,
      selectedFundingGroup: proposal.local.fundingGroup ?? null,
      selectedDownPayment,
      selectedFinanceProductId,
      selectedProduct,
      selectedUpdatedPricePerWatt,
    })

    const noChanges =
      payload.financeProductId === (proposal.local.financeProductId ?? null) &&
      payload.selectedLoanProductId ===
        (proposal.local.selectedLoanProduct?.id ?? null) &&
      payload.downPayment === (proposal.local.downPayment ?? null)

    const previousFinanceSelection = {
      selectedLoanProduct: proposal.local.selectedLoanProduct,
      selectedLoanProductId: proposal.local.selectedLoanProductId,
      financeProductId: proposal.local.financeProductId,
      downPayment: proposal.local.downPayment,
    }

    const applyLocalFinanceSelection = () => {
      proposal.setLocal((curr) => {
        const next = { ...curr }

        const currentFinanceType = curr.financeType
        const isCash = currentFinanceType === FinanceType.CASH
        const isCreditHuman = currentFinanceType === FinanceType.CREDIT_HUMAN
        const isLightReach = currentFinanceType === FinanceType.LIGHT_REACH

        if (!isLightReach) {
          next.selectedLoanProduct = selectedProduct ?? null
          next.selectedLoanProductId = selectedProduct?.id ?? null
        } else {
          next.selectedLoanProduct = null
          next.selectedLoanProductId = null
        }

        if (isLightReach) {
          next.financeProductId = payload.financeProductId ?? null
        } else {
          next.financeProductId = null
        }

        if (isCash || isCreditHuman || isLightReach) {
          next.downPayment = null
        } else {
          next.downPayment = payload.downPayment ?? null
        }

        return next
      })
    }

    if (
      selectedProduct?.provider === `goodleap` &&
      selectedProduct.loanType === `ppa`
    ) {
      await createLoanProduct.mutateAsync({
        input: buildCreateLoanProductInput(selectedProduct),
      })
    }

    // Switch savings readiness to the new selection before the mutation's
    // invalidation pipeline can refetch the previously active savings query.
    applyLocalFinanceSelection()

    if (!noChanges) {
      try {
        await updateProposalFinanceProduct.mutateAsync({
          id: proposal.local.id ?? ``,
          data: payload,
        })

        await queryClient.refetchQueries({
          queryKey: queryKeys.proposal.savings(proposal.local.id),
          type: `active`,
        })
        setSaveError(null)
      } catch (error) {
        const message =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.response?.data?.message ??
          (error as Error)?.message ??
          `Something went wrong`
        proposal.setLocal((curr) => ({
          ...curr,
          ...previousFinanceSelection,
        }))
        setSaveError(message)
        onError(error)
        throw error
      }
    }

    setHasUserModifiedFunding(false)

    const isLightReachFinance =
      (proposal.local.financeType ?? FinanceType.CASH) ===
      FinanceType.LIGHT_REACH

    if (isLightReachFinance) {
      await proposal.refetch()
    }
  }, [
    createLoanProduct,
    onError,
    proposal,
    queryClient,
    selectedDownPayment,
    selectedFinanceProductId,
    selectedProduct,
    selectedUpdatedPricePerWatt,
    setHasUserModifiedFunding,
    setSaveError,
    updateProposalFinanceProduct,
  ])

  return { handleSaveLoanProduct }
}
